import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type Temporal } from 'temporal-polyfill';

import { formatInstant, formatTimeRange, FrequentTimeZones } from '@airreading/common';

import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog.js';
import { Button } from '@/components/ui/button.js';
import { LoaderIcon } from '@/components/ui/loader-icon.js';
import { getCoachTqReviewSessions } from '@/lib/api-calls.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { cn } from '@/lib/tailwind-utils.js';

import {
  TqReviewClassNotesCell,
  TqReviewLessonCell,
  TqReviewRecordingCell,
  TqReviewStudentIssuesCell,
  TqReviewStudentsCell,
} from './tq-review-session-cells.js';

type PmGlaSessionsDialogProps = {
  reviewId: string;
  moreCount: number;
};

const headerCellClassName = 'border px-2 py-2 font-semibold text-foreground whitespace-nowrap';
const bodyCellClassName = 'border px-2 py-2 align-middle';

function formatSessionDateAndTimes(start: Temporal.Instant, end: Temporal.Instant): string {
  const date = formatInstant(start, FrequentTimeZones.UsCentral).formattedDate;
  const times = formatTimeRange(start, end, FrequentTimeZones.UsCentral).toUpperCase();
  return `${date}, ${times}`;
}

export function PmGlaSessionsDialog({ reviewId, moreCount }: PmGlaSessionsDialogProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { data, isPending, error } = useQuery({
    ...getCoachTqReviewSessions(reviewId),
    enabled: isOpen,
  });

  useErrorToast(error, 'Error loading PM/GLA sessions');

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="link"
          className={cn('h-auto p-0 text-blue-600 hover:text-blue-800 whitespace-nowrap')}
          aria-label={`Show ${moreCount} more PM/GLA sessions`}
          data-testid={`teachers-tq-review-button-more-sessions-${reviewId}`}
        >
          {`+${moreCount} more`}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-5xl max-h-[85vh] overflow-y-auto p-4"
        data-testid="teachers-tq-review-dialog-pm-gla-sessions"
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">PM/GLA sessions</DialogTitle>
        {isPending ? (
          <div className="flex justify-center py-8">
            <LoaderIcon />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border text-sm text-left">
              <thead>
                <tr>
                  <th className={headerCellClassName}>Date and times (CT)</th>
                  <th className={headerCellClassName}>Lesson</th>
                  <th className={headerCellClassName}>Recording</th>
                  <th className={headerCellClassName}>Students</th>
                  <th className={headerCellClassName}>Student issues</th>
                  <th className={headerCellClassName}>Class notes</th>
                </tr>
              </thead>
              <tbody>
                {(data?.sessions ?? []).map((session) => (
                  <tr key={session.sessionId}>
                    <td className={cn(bodyCellClassName, 'whitespace-nowrap')}>
                      <Link
                        to="/sessions/$sessionId"
                        params={{ sessionId: session.sessionId }}
                        search={{ from: 'past' }}
                        className="text-foreground hover:underline"
                      >
                        {formatSessionDateAndTimes(session.startInstant, session.endInstant)}
                      </Link>
                    </td>
                    <td className={bodyCellClassName}>
                      <TqReviewLessonCell lessonName={session.lessonName} lessonId={session.lessonId} compact />
                    </td>
                    <td className={bodyCellClassName}>
                      <TqReviewRecordingCell recordingUrls={session.recordingUrls} compact />
                    </td>
                    <td className={bodyCellClassName}>
                      <TqReviewStudentsCell students={session.students} compact />
                    </td>
                    <td className={bodyCellClassName}>
                      <TqReviewStudentIssuesCell studentIssues={session.studentIssues} compact />
                    </td>
                    <td className={bodyCellClassName}>
                      <TqReviewClassNotesCell classNotes={session.classNotes} rowId={session.sessionId} compact />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(data?.sessions?.length ?? 0) === 0 && (
              <div className="py-6 text-center text-muted-foreground">No associated sessions</div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
